'use client';

import { useState, useEffect, useCallback, useRef } from 'react';
import toast from 'react-hot-toast';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import type { PendingRequestWithEmployee } from './types';

interface PendingApprovalsPanelProps {
  approverEmployeeId: string | null;
  onResolved: () => void;
}

const BALANCE_LABELS: Record<string, { label: string; color: string }> = {
  pto:               { label: 'PTO',              color: '#E89A3C' },
  sick:              { label: 'Sick',             color: '#dc2626' },
  bereavement:       { label: 'Bereavement',      color: '#6b7280' },
  jury_duty:         { label: 'Jury Duty',        color: '#7c3aed' },
  parental:          { label: 'Parental Leave',   color: '#16a34a' },
  floating_holiday:  { label: 'Floating Holiday', color: '#F5A623' },
  unpaid:            { label: 'Unpaid',           color: '#6b7280' },
  other:             { label: 'Other',            color: '#6b7280' },
};

const PendingApprovalsPanel = ({ approverEmployeeId, onResolved }: PendingApprovalsPanelProps) => {
  const [pending, setPending] = useState<PendingRequestWithEmployee[]>([]);
  const [recentlyResolved, setRecentlyResolved] = useState<PendingRequestWithEmployee[]>([]);
  const [loading, setLoading] = useState(true);
  const [showResolved, setShowResolved] = useState(false);
  const [resolvingId, setResolvingId] = useState<string | null>(null);
  const [avatarMap, setAvatarMap] = useState<Record<string, string>>({});

  const supabase = createClient();
  const { currentUser } = useAuth();
  const refreshTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const loadRequests = useCallback(async () => {
    if (!approverEmployeeId) {
      setLoading(false);
      return;
    }

    // Trigger lazy escalation on every load
    try {
      await supabase.rpc('auto_escalate_stale_requests');
    } catch (err) {
      console.warn('[Approvals] Auto-escalation failed (non-fatal):', err);
    }

    // Pending requests scoped to this approver
    const { data: pendingRows, error: pendErr } = await supabase
      .from('employee_time_off_requests')
      .select('*')
      .eq('current_approver_id', approverEmployeeId)
      .eq('status', 'pending')
      .order('created_at', { ascending: true });

    if (pendErr) {
      console.error('[Approvals] Pending fetch failed:', pendErr);
      setLoading(false);
      return;
    }

    // Resolved (last 30 days) — anything resolved by current user
    const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString();
    const { data: resolvedRows } = await supabase
      .from('employee_time_off_requests')
      .select('*')
      .eq('resolved_by_id', approverEmployeeId)
      .in('status', ['approved', 'denied'])
      .gte('resolved_at', thirtyDaysAgo)
      .order('resolved_at', { ascending: false })
      .limit(20);

    // Hydrate joined data sequentially to avoid 429s
    const hydrate = async (rows: any[]): Promise<PendingRequestWithEmployee[]> => {
      const result: PendingRequestWithEmployee[] = [];
      for (const row of rows) {
        const { data: emp } = await supabase
          .from('employees')
          .select('first_name, last_name, avatar_url, job_title')
          .eq('id', row.employee_id)
          .maybeSingle();
        await new Promise(r => setTimeout(r, 60));
        const { data: bal } = await supabase
          .from('employee_time_off_balances')
          .select('hours_remaining, hours_accrued')
          .eq('employee_id', row.employee_id)
          .eq('balance_type', row.balance_type)
          .maybeSingle();
        await new Promise(r => setTimeout(r, 60));

        result.push({
          ...row,
          requester_first_name: emp?.first_name ?? null,
          requester_last_name: emp?.last_name ?? null,
          requester_avatar_url: emp?.avatar_url ?? null,
          requester_job_title: emp?.job_title ?? null,
          current_balance_remaining: bal?.hours_remaining ?? null,
          current_balance_total: bal?.hours_accrued ?? null,
        });
      }
      return result;
    };

    const [pendingHydrated, resolvedHydrated] = [await hydrate(pendingRows ?? []), await hydrate(resolvedRows ?? [])];
    setPending(pendingHydrated);
    setRecentlyResolved(resolvedHydrated);
    setLoading(false);

    // Resolve avatar URLs for storage paths
    const allRows = [...pendingHydrated, ...resolvedHydrated];
    const newAvatarMap: Record<string, string> = {};
    for (const row of allRows) {
      if (row.requester_avatar_url && !row.requester_avatar_url.startsWith('http')) {
        try {
          const r = await fetch('/api/hr/document-download-url', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ storage_path: row.requester_avatar_url }),
          });
          const data = await r.json();
          if (data.signed_url) newAvatarMap[row.id] = data.signed_url;
        } catch {/* silent */}
      } else if (row.requester_avatar_url) {
        newAvatarMap[row.id] = row.requester_avatar_url;
      }
    }
    setAvatarMap(newAvatarMap);
  }, [approverEmployeeId, supabase]);

  // Debounced reload on realtime changes
  const debouncedReload = useCallback(() => {
    if (refreshTimeoutRef.current) clearTimeout(refreshTimeoutRef.current);
    refreshTimeoutRef.current = setTimeout(() => loadRequests(), 500);
  }, [loadRequests]);

  useEffect(() => {
    loadRequests();
    const channel = supabase
      .channel('pto_requests_admin')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'employee_time_off_requests' }, () => debouncedReload())
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [loadRequests, debouncedReload, supabase]);

  const handleApprove = async (req: PendingRequestWithEmployee) => {
    if (resolvingId) return;
    setResolvingId(req.id);

    // Warn but allow if balance insufficient
    const reqHours = Number(req.hours_requested);
    const remaining = req.current_balance_remaining ?? 0;
    if (reqHours > remaining) {
      const proceed = confirm(
        `${req.requester_first_name} is requesting ${reqHours}h of ${BALANCE_LABELS[req.balance_type]?.label ?? req.balance_type} but only has ${remaining.toFixed(1)}h available. Approving will put their balance at ${(remaining - reqHours).toFixed(1)}h. Continue?`
      );
      if (!proceed) {
        setResolvingId(null);
        return;
      }
    }

    const approverName = `${currentUser?.first_name ?? ''} ${currentUser?.last_name ?? ''}`.trim();
    console.log(`[Approvals] Approving ${req.id} for ${req.requester_first_name}`);

    for (let attempt = 0; attempt < 3; attempt++) {
      const timeoutPromise = new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error(`Approve timed out (attempt ${attempt + 1}/3)`)), 8000));

      const opPromise = supabase.rpc('apply_pto_approval', {
        request_id: req.id,
        approver_id: approverEmployeeId,
        approver_name: approverName,
        resolution_note: null,
      }).then((res: any) => { if (res.error) throw res.error; return res.data; });

      try {
        await Promise.race([opPromise, timeoutPromise]);
        console.log(`[Approvals] Approve succeeded attempt ${attempt + 1}`);
        toast.success(`Approved ${reqHours}h ${BALANCE_LABELS[req.balance_type]?.label ?? req.balance_type} for ${req.requester_first_name}`, { duration: 3500, icon: '✅' });
        // Optimistically remove the card
        setPending(prev => prev.filter(r => r.id !== req.id));
        onResolved();
        setResolvingId(null);
        return;
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        const isRateLimit = msg.includes('429') || msg.toLowerCase().includes('too many');
        const isTimeout = msg.includes('timed out');
        console.warn(`[Approvals] Approve attempt ${attempt + 1} failed: ${msg}`);
        if (attempt === 2 || (!isRateLimit && !isTimeout)) {
          toast.error(`Approve failed: ${msg}`, { duration: 6000 });
          setResolvingId(null);
          return;
        }
        await new Promise(r => setTimeout(r, 500 * Math.pow(2, attempt)));
      }
    }
  };

  const handleDeny = async (req: PendingRequestWithEmployee) => {
    if (resolvingId) return;
    const reason = prompt(`Why are you denying ${req.requester_first_name}'s ${req.hours_requested}h request? They will see this message.`);
    if (reason === null) return;
    if (reason.trim().length < 3) {
      toast.error('Please provide a reason (at least 3 characters)');
      return;
    }
    setResolvingId(req.id);

    const approverName = `${currentUser?.first_name ?? ''} ${currentUser?.last_name ?? ''}`.trim();
    console.log(`[Approvals] Denying ${req.id} for ${req.requester_first_name}`);

    for (let attempt = 0; attempt < 3; attempt++) {
      const timeoutPromise = new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error(`Deny timed out (attempt ${attempt + 1}/3)`)), 8000));

      const opPromise = supabase.rpc('deny_pto_request', {
        request_id: req.id,
        approver_id: approverEmployeeId,
        approver_name: approverName,
        reason: reason.trim(),
      }).then((res: any) => { if (res.error) throw res.error; return res.data; });

      try {
        await Promise.race([opPromise, timeoutPromise]);
        console.log(`[Approvals] Deny succeeded attempt ${attempt + 1}`);
        toast(`Denied request from ${req.requester_first_name}`, { duration: 3500, icon: '❌' });
        setPending(prev => prev.filter(r => r.id !== req.id));
        onResolved();
        setResolvingId(null);
        return;
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        const isRateLimit = msg.includes('429') || msg.toLowerCase().includes('too many');
        const isTimeout = msg.includes('timed out');
        console.warn(`[Approvals] Deny attempt ${attempt + 1} failed: ${msg}`);
        if (attempt === 2 || (!isRateLimit && !isTimeout)) {
          toast.error(`Deny failed: ${msg}`, { duration: 6000 });
          setResolvingId(null);
          return;
        }
        await new Promise(r => setTimeout(r, 500 * Math.pow(2, attempt)));
      }
    }
  };

  if (!approverEmployeeId) return null;
  if (loading) {
    return (
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 mb-5">
        <div className="flex items-center gap-2 text-gray-500 text-sm">
          <div className="w-4 h-4 rounded-full border-2 border-[#E89A3C] border-t-transparent animate-spin" />
          Loading pending approvals...
        </div>
      </div>
    );
  }

  if (pending.length === 0 && recentlyResolved.length === 0) return null;

  return (
    <div className="mb-5 space-y-3">
      {pending.length > 0 && (
        <div className="bg-white rounded-2xl shadow-sm border border-amber-200 overflow-hidden">
          <div className="bg-gradient-to-r from-amber-50 to-amber-100 px-5 py-3 border-b border-amber-200 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5 text-amber-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2" strokeLinecap="round"/></svg>
              <h3 className="text-[14px] font-bold text-amber-900">Pending Approvals — {pending.length}</h3>
            </div>
            <p className="text-[10px] text-amber-700">Awaiting your decision</p>
          </div>
          <div className="divide-y divide-gray-100">
            {pending.map(req => {
              const balCfg = BALANCE_LABELS[req.balance_type] ?? BALANCE_LABELS.other;
              const isEscalated = req.escalated;
              const isInsufficient = (req.current_balance_remaining ?? 0) < req.hours_requested;
              const dateLabel = req.start_date === req.end_date
                ? new Date(req.start_date + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
                : `${new Date(req.start_date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} – ${new Date(req.end_date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
              const ageHours = Math.floor((Date.now() - new Date(req.created_at).getTime()) / (60 * 60 * 1000));
              const ageLabel = ageHours < 1 ? 'just now' : ageHours < 24 ? `${ageHours}h ago` : `${Math.floor(ageHours / 24)}d ago`;
              const balanceAfter = (req.current_balance_remaining ?? 0) - Number(req.hours_requested);

              return (
                <div key={req.id} className="px-5 py-4 hover:bg-gray-50 transition-colors">
                  <div className="flex items-start gap-3">
                    {avatarMap[req.id] ? (
                      <img src={avatarMap[req.id]} alt="" className="w-10 h-10 rounded-full object-cover flex-shrink-0" />
                    ) : (
                      <div
                        className="w-10 h-10 rounded-full flex items-center justify-center text-white text-[11px] font-bold flex-shrink-0"
                        style={{ background: balCfg.color }}
                      >
                        {`${req.requester_first_name?.[0] ?? ''}${req.requester_last_name?.[0] ?? ''}`.toUpperCase()}
                      </div>
                    )}

                    <div className="min-w-0 flex-1">
                      <div className="flex items-baseline gap-2 flex-wrap">
                        <p className="text-[13px] font-bold text-[#1C2E5E]">
                          {req.requester_first_name} {req.requester_last_name}
                        </p>
                        <span className="text-[10px] text-gray-500">{req.requester_job_title}</span>
                        {isEscalated && <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[9px] font-bold bg-orange-100 text-orange-700">ESCALATED</span>}
                      </div>

                      <div className="flex items-baseline gap-2 mt-1 flex-wrap">
                        <span className="text-[14px] font-bold" style={{ color: balCfg.color }}>
                          {Number(req.hours_requested).toFixed(req.hours_requested % 1 === 0 ? 0 : 1)}h {balCfg.label}
                        </span>
                        <span className="text-[11px] text-gray-600">·</span>
                        <span className="text-[12px] text-gray-700">{dateLabel}</span>
                        <span className="text-[10px] text-gray-400">· submitted {ageLabel}</span>
                      </div>

                      {req.notes && (
                        <p className="text-[11px] text-gray-600 italic mt-1.5 line-clamp-2" title={req.notes}>
                          &ldquo;{req.notes}&rdquo;
                        </p>
                      )}

                      <div className="mt-2 text-[10px]">
                        {isInsufficient ? (
                          <span className="text-amber-700 font-bold">
                            ⚠ {req.requester_first_name} has {(req.current_balance_remaining ?? 0).toFixed(1)}h available · would go to {balanceAfter.toFixed(1)}h after approval
                          </span>
                        ) : (
                          <span className="text-gray-500">
                            Balance after approval: {balanceAfter.toFixed(1)}h remaining
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-2 flex-shrink-0">
                      <button
                        type="button"
                        onClick={() => handleDeny(req)}
                        disabled={resolvingId === req.id}
                        className="px-3 py-1.5 rounded-lg text-[11px] font-bold text-red-700 bg-red-50 hover:bg-red-100 disabled:opacity-50 transition-colors"
                      >
                        Deny
                      </button>
                      <button
                        type="button"
                        onClick={() => handleApprove(req)}
                        disabled={resolvingId === req.id}
                        className="px-3 py-1.5 rounded-lg text-[11px] font-bold text-white bg-[#16a34a] hover:bg-[#15803d] disabled:opacity-50 transition-colors"
                      >
                        {resolvingId === req.id ? 'Approving…' : 'Approve'}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {recentlyResolved.length > 0 && (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          <button
            type="button"
            onClick={() => setShowResolved(prev => !prev)}
            className="w-full px-5 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors"
          >
            <div className="flex items-center gap-2">
              <svg className="w-4 h-4 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14M22 4 12 14.01l-3-3" strokeLinecap="round" strokeLinejoin="round"/></svg>
              <span className="text-[12px] font-bold text-gray-700">Recently Resolved — last 30 days ({recentlyResolved.length})</span>
            </div>
            <svg className={`w-4 h-4 text-gray-400 transition-transform ${showResolved ? 'rotate-180' : ''}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="m6 9 6 6 6-6" strokeLinecap="round" strokeLinejoin="round"/></svg>
          </button>
          {showResolved && (
            <div className="divide-y divide-gray-100 border-t border-gray-100">
              {recentlyResolved.map(req => {
                const balCfg = BALANCE_LABELS[req.balance_type] ?? BALANCE_LABELS.other;
                const dateLabel = req.start_date === req.end_date
                  ? new Date(req.start_date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                  : `${new Date(req.start_date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} – ${new Date(req.end_date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
                const isApproved = req.status === 'approved';
                return (
                  <div key={req.id} className="px-5 py-2.5 flex items-center gap-3 text-[11px]">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[9px] font-bold flex-shrink-0 ${isApproved ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                      {isApproved ? 'APPROVED' : 'DENIED'}
                    </span>
                    <span className="font-semibold text-gray-700">{req.requester_first_name} {req.requester_last_name}</span>
                    <span className="text-gray-500">·</span>
                    <span style={{ color: balCfg.color }} className="font-medium">{Number(req.hours_requested).toFixed(req.hours_requested % 1 === 0 ? 0 : 1)}h {balCfg.label}</span>
                    <span className="text-gray-500">·</span>
                    <span className="text-gray-600">{dateLabel}</span>
                    {req.resolution_notes && !isApproved && (
                      <span className="text-gray-500 italic truncate" title={req.resolution_notes}>&ldquo;{req.resolution_notes}&rdquo;</span>
                    )}
                    <span className="text-[9px] text-gray-400 ml-auto flex-shrink-0">{new Date(req.resolved_at!).toLocaleDateString()}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PendingApprovalsPanel;
